import { Injector } from '@angular/core';
import { deleteShapes } from '../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { ImageFromDataState } from '../../../../cross-team-shared/state/image-from-data-state';
import { addUngroupedShape } from '../../../../cross-team-shared/state/shape.state';
import { distanceBetween } from '../../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { WHITE_CODE } from '../../state/image-content-state';
import {
  copyMask,
  newMaskForLayer,
  setPixelXY,
  toMaskPositionXY,
} from '../../utils/mask-utils';
import { AbstractRasterDragHandler } from '../abstract-raster-drag-handler';

const LINE_INDEX = 1;

export class FloodfillDragHandler extends AbstractRasterDragHandler {
  protected override dedupeDragsPerImagePoint = false;

  private startPoint: Point2D;
  private startContainerPoint: Point2D;
  private tolerance!: number;
  private endMask!: Uint8Array;
  private pointStack!: Point2D[];
  private lineStore = this.store('vectorOverlay')('shapes')(LINE_INDEX);

  // optimization: cache these; going to the store for them is too slow
  private image: ImageFromDataState;

  constructor(code: number, initialEvent: MouseEvent, injector: Injector) {
    super(code, initialEvent, injector);

    this.image = this.store.state().sensorRendererState;
    this.startPoint = this.getImagePoint();
    this.startContainerPoint = this.getContainerPoint();

    const line: ShapeState2D = {
      type: 'polygon',
      index: LINE_INDEX,
      tags: {},
      dynamic_tags: {},
      key_locations: [{ frame_number: 0, points: [], visibility: 1 }],
    };
    addUngroupedShape(line, this.store('vectorOverlay'));
  }

  protected onDrag(dragPoint: Point2D) {
    this.updateTolerance();
    this.endMask = newMaskForLayer(this.store.state().sensorRendererState);
    this.floodfill(this.code || WHITE_CODE);
    this.store.batch(() => {
      this.store.getRasterOverlayStore()('rawMask').set(this.endMask);
      // @ts-ignore -- fix me
      this.lineStore('key_locations')(0)('points').set([
        this.startPoint,
        dragPoint,
      ]);
    });
  }

  protected override complete() {
    this.updateTolerance();
    this.endMask = copyMask(this.store.getRawMask());
    this.floodfill(this.code);
    this.store.batch(() => {
      this.store('rawMask').set(this.endMask);
      this.store
        .getRasterOverlayStore()('rawMask')
        .set(newMaskForLayer(this.store.state().sensorRendererState));
      deleteShapes([LINE_INDEX], this.store('vectorOverlay'));
    });

    const batchValues = this.getBatchValues();
    this.shellConnector.pushAndTrack({
      ...this.event,
      tool: 'fill',
      ...batchValues.buildEventTrackingValue(),
    });
  }

  protected getContainerPoint() {
    return this.store.state().mouse.containerPoint;
  }

  private updateTolerance() {
    this.tolerance = distanceBetween(
      this.startContainerPoint,
      this.getContainerPoint(),
    );
  }

  // Started from https://github.com/williammalone/HTML5-Paint-Bucket-Tool
  // eslint-disable-next-line complexity -- if you need to modify this code, fix this first
  private floodfill(code: number) {
    this.pointStack = [this.startPoint];
    const { width, height } = this.store.state().sensorRendererState;

    let point: Point2D;
    let x: number;
    let y: number;
    do {
      point = this.pointStack.pop()!;
      [x, y] = point;

      // scan up as long as there is a match
      while (y > 0 && this.needsPaint(x, y - 1, code)) {
        --y;
      }

      // paint down as long as there is a match
      let reachedLeft = false;
      let reachedRight = false;
      const sensorRendererState = this.store.state().sensorRendererState;
      do {
        setPixelXY(x, y, code, sensorRendererState, this.endMask);

        if (x > 0) {
          reachedLeft = this.checkPixel(x - 1, y, reachedLeft, code);
        }
        if (x < width - 1) {
          reachedRight = this.checkPixel(x + 1, y, reachedRight, code);
        }

        ++y;
      } while (y < height && this.needsPaint(x, y, code));
    } while (this.pointStack.length);
  }

  private checkPixel(
    x: number,
    y: number,
    reached: boolean,
    code: number,
  ): boolean {
    if (!this.needsPaint(x, y, code)) {
      return false;
    }

    if (!reached) {
      this.pointStack.push([x, y]);
    }
    return true;
  }

  private needsPaint(x: number, y: number, code: number) {
    const curPos = this.toMaskPosition(x, y);
    const startPos = this.toMaskPosition(
      this.startPoint[0],
      this.startPoint[1],
    );
    const startMask = this.store.getRawMask();
    return (
      this.endMask[curPos] !== code &&
      startMask[curPos] === startMask[startPos] &&
      this.isWithinTolerance(x, y)
    );
  }

  private isWithinTolerance(x: number, y: number) {
    if (this.tolerance >= 255) {
      // Optimization: don't bother if we know the tolerance lets anything by.
      // Speeds up worst case floodfill by ~45% in testing.
      return true;
    }

    // source pixels are cached on tool activation
    const srcPixels = this.image.data!;
    let pos = this.toImagePosition(x, y);
    const r = srcPixels[pos];
    const g = srcPixels[pos + 1];
    const b = srcPixels[pos + 2];

    // fix me if you change this code
    // eslint-disable-next-line no-param-reassign -- fix me!
    [x, y] = this.startPoint;
    pos = this.toImagePosition(x, y);
    const startR = srcPixels[pos];
    const startG = srcPixels[pos + 1];
    const startB = srcPixels[pos + 2];

    return (
      Math.abs(r - startR) < this.tolerance &&
      Math.abs(g - startG) < this.tolerance &&
      Math.abs(b - startB) < this.tolerance
    );
  }

  private toImagePosition(x: number, y: number) {
    return 4 * this.toMaskPosition(x, y);
  }

  private toMaskPosition(x: number, y: number) {
    return toMaskPositionXY(x, y, this.store.state().sensorRendererState);
  }
}
